import { effect, inject, Injectable, signal } from '@angular/core';
import { AuthService, NotificationI, SignalRService } from 'shared-library';

/** A notification as the hub delivers it; the fields after message are only present when the backend sends them. */
export interface PortalNotification extends NotificationI {
  notificationType?: string | null;
  assignmentId?: string | null;
  assignmentCode?: string | null;
  fromUserName?: string | null;
  createdAt?: string | null;
}

/**
 * Notifications of the signed-in investigator, delivered by the backend through the SignalR hub
 * (unread ones on connect, new ones live) and marked as read with POST notifications/read.
 */
@Injectable({ providedIn: 'root' })
export class PortalNotificationService {
  private readonly auth = inject(AuthService);
  private readonly hub = inject(SignalRService);

  /** Unread notifications, newest first. */
  readonly items = signal<PortalNotification[]>([]);
  /** The newest notification that was not known before; the open pages use it to refresh what they show. */
  readonly arrived = signal<PortalNotification | null>(null);

  constructor() {
    effect(() => {
      const incoming = this.hub.notifications() as PortalNotification[];
      if (!incoming.length) return;
      this.items.update((current) => {
        const known = new Set(current.map((item) => item.id));
        const fresh = incoming.filter((item) => !known.has(item.id));
        if (fresh.length) this.arrived.set(fresh[fresh.length - 1]);
        return fresh.length ? [...fresh.reverse(), ...current] : current;
      });
    }, { allowSignalWrites: true });
  }

  /** Opens the hub with the stored token; the shared login only does it right after a fresh sign-in, not on a page reload. */
  connect(): void {
    this.hub.setToken(this.auth.getCurrentToken());
    this.hub.startConnection();
  }

  disconnect(): void {
    this.hub.destroyConnection();
  }

  markAsRead(ids: string[]): void {
    if (!ids.length) return;
    this.hub.markAsRead(ids).subscribe({
      next: () => this.items.update((current) => current.filter((item) => !ids.includes(item.id))),
      error: () => undefined, // stays in the list; the next click retries
    });
  }

  markAllAsRead(): void {
    this.markAsRead(this.items().map((item) => item.id));
  }
}
